import {
  AbstractControl,
  FormControl, isFormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';

import { validatePolish } from 'validate-polish';
import { isValidDate } from 'rxjs/internal/util/isDate';
import { clearDate } from '../../services/helpers';

export const mailformat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

export const dateFormat = /\d{2}.\d{2}.\d{4}/;

export const bankAccountRegex = `^(\\d{26}|\\d{2} (\\d{4} ){5}\\d{4})$`;

export class CustomValidator {

  public static bankAccountNumber(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      if (new RegExp(bankAccountRegex).test(control.value)) {
        return null;
      } else {
        return {
          invalidBankAccountNumber: true
        };
      }
    };
  }

  public static textarea(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      if (control.value?.length >= 5000) {
        return { maxLength: 5000 };
      }
      return null;
    };
  }

  public static isValidDateFormat(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      if (typeof control.value === 'string') {
        const regex = new RegExp(dateFormat);
        return regex.test(control.value) ? null : { invalidDate: true };
      } else {
        return null;
      }
    };
  }

  public static maxDate(d: Date): ValidatorFn {
    d.setHours(0, 0, 0, 0);
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      if (isValidDate(control.value)) {
        const date = new Date(control.value);
        date.setHours(0, 0, 0, 0);

        if (date.getFullYear().toString().length < 4) {
          return { invalidDate: true };
        }

        if (date.getTime() > d.getTime()) {
          return { maxDate: d };
        }
      }
      return null;
    };
  }

  public static minDate(d: Date): ValidatorFn {
    d.setHours(0, 0, 0, 0);
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      if (isValidDate(control.value)) {
        const date = new Date(control.value);
        date.setHours(0, 0, 0, 0);

        if (date.getFullYear().toString().length < 4) {
          return { invalidDate: true };
        }

        if (date.getTime() < d.getTime()) {
          return { minDate: d };
        }
      }
      return null;
    };
  }

  public static isObject(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return (typeof control.value === 'object' && control.value !== null) ? null : { required: true };
    };
  }

  public static email(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === '' || !control?.value) {
        return null;
      }
      return control.value.match(mailformat) ? null : { email: true };
    };
  }

  public static phone(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return null;
      // if (control.value === null || control.value === '') {
      //   return null;
      // }
      // return (control.value.length >= 9) && (control.value.length <= 9) ? null : { phone: true };
    };
  }

  public static pesel(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === '') {
        return null;
      }
      return validatePolish.pesel(control.value + '') ? null : { pesel: true };
    };
  }

  public static nip(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === '') {
        return null;
      }
      if (!validatePolish.nip(control.value)) {
        control.setErrors({ nip: true });
        return { nip: true };
      }
      return null;
    };
  }

  public static regon(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === null || control.value === '') {
        return null;
      }
      if (!validatePolish.regon(control.value)) {
        control.setErrors({ regon: true });
        return { regon: true };
      }
      return null;
    };
  }
}

export function optional(controlName: string, secondControlName: string): ValidatorFn {
  return (formGroup: AbstractControl) => {
    const control = formGroup.get(controlName);
    const secondControl = formGroup.get(secondControlName);
    if (!control || !secondControl) {
      return null;
    }
    if (control.value || secondControl.value) {
      control.setErrors(null);
      secondControl.setErrors(null);
      return null;
    } else {
      control.setErrors({ required: true });
      secondControl.setErrors({ required: true });
      return null;
    }
  };
}

export function bothRequired(controlName: string, secondControlName: string): ValidatorFn {
  return (formGroup: AbstractControl) => {
    const control = formGroup.get(controlName);
    const secondControl = formGroup.get(secondControlName);
    if (!control?.value && !secondControl?.value) {
      secondControl?.setErrors(null);
      control?.setErrors(null);
      return null;
    }
    if (control?.value) {
      secondControl?.setErrors({ required: true });
    }
    if (secondControl?.value) {
      control?.setErrors({ required: true });
    }
    if (secondControl?.value) {
      secondControl.setErrors(null);
    }
    if (control?.value) {
      control?.setErrors(null);
    }
    return null;
  };
}

export function fromToDate(controlName: string, secondControlName: string): ValidatorFn {
  return (formGroup: AbstractControl): { [key: string]: any } | null => {
    const control = formGroup.get(controlName) as FormControl;
    const secondControl = formGroup.get(secondControlName);
    if (!control?.value || !secondControl?.value) {
      return null;
    }
    if (isValidDate(control.value) && isValidDate(secondControl.value)) {
      const date = new Date(control.value);
      date.setHours(0, 0, 0, 0);
      const secondDate = new Date(secondControl.value);
      secondDate.setHours(0, 0, 0, 0);

      if (secondDate.getTime() < date.getTime()) {
        secondControl.setErrors({ fromToDate: false });
      }
    }
    return null;
  };
}

export function fromToTime(controlName: string, secondControlName: string, dateControlName?: string, dateSecondControlName?: string): ValidatorFn {
  return (formGroup: AbstractControl): { [key: string]: any } | null => {
    const control = formGroup.get(controlName) as FormControl;
    const secondControl = formGroup.get(secondControlName);
    const dateControl = formGroup.get(dateControlName || '');
    const dateSecondControl = formGroup.get(dateSecondControlName || '');

    if (!control?.value || !secondControl?.value) {
      return null;
    }
    const time: string[] = control.value?.split(':') || [];
    const secondTime: string[] = secondControl.value?.split(':') || [];

    if ((typeof dateControl?.value === 'object') && (typeof dateSecondControl?.value === 'object')) {
      if ((time.length === 2) && (secondTime.length === 2)) {
        const date = new Date(dateControl.value);
        date.setHours(+time[0], +time[1], 0, 0);
        const secondDate = new Date(dateSecondControl.value);
        secondDate.setHours(+secondTime[0], +secondTime[1], 0, 0);
        if (secondDate.getTime() < date.getTime()) {
          secondControl.setErrors({ fromToTime: true });
        }
      }

    }

    if (!dateControl && !dateSecondControl) {
      if ((time.length === 2) && (secondTime.length === 2)) {
        const date = new Date();
        date.setHours(+time[0], +time[1], 0, 0);
        const secondDate = new Date();
        secondDate.setHours(+secondTime[0], +secondTime[1], 0, 0);
        if (secondDate.getTime() < date.getTime()) {
          secondControl.setErrors({ fromToTime: true });
        }
      }
    }

    return null;
  };
}

export function fromToYears(controlName: string, secondControlName: string): ValidatorFn {
  return (formGroup: AbstractControl): { [key: string]: any } | null => {
    const control = formGroup.get(controlName) as FormControl;
    const secondControl = formGroup.get(secondControlName);
    if (!control?.value || !secondControl?.value) {
      return null;
    }
    if (+secondControl?.value < +control?.value) {
      secondControl.setErrors({ fromToYears: true });
    }
    return null;
  };
}
